import React, {
	Ref,
	forwardRef,
	useState,
	useEffect,
	useMemo,
	useCallback,
	ChangeEventHandler,
	ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { getInputBaseClassName } from '../InputBase';

type DropdownSimpleItem = {
	label: string;
	value: string | number | null;
};

type DropdownSimpleProps = {
	options?: DropdownSimpleItem[];
	withEmpty?: boolean;
	emptyLabel?: string;
	emptyValue?: any;
	value?: string | number | null;
	defaultValue?: string | number | null;
	onChange?: (newValue: string | number | null) => void;
	variant?: 'default' | 'primary-outlined';
	renderLeft?: () => ReactNode;
};

export const DropdownSimple = forwardRef(
	(
		{
			options,
			withEmpty,
			emptyLabel,
			emptyValue,
			value,
			defaultValue,
			onChange,
			variant,
			renderLeft,
		}: DropdownSimpleProps,
		ref: Ref<HTMLSelectElement>
	) => {
		const { t } = useTranslation();
		const [currentValueTemp, setCurrentValue] = useState<string | number | null>(
			typeof value === 'undefined' || value === null ? defaultValue || null : value
		);
		const currentValue = currentValueTemp || -1;

		useEffect(() => {
			if (value !== currentValue) {
				setCurrentValue(value || null);
			}
		}, [value, currentValue]);

		const emptyOption = { value: emptyValue, label: emptyLabel || t('misc.dropdownPleaseSelect') || '' };
		const finalOptions: DropdownSimpleItem[] = useMemo(
			() => (withEmpty ? [emptyOption, ...(options || [])] : options || []),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[withEmpty, options]
		);

		const handleChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
			({ currentTarget: { value: newValue } }) => {
				const optionValue = options?.find(({ value }) => newValue === String(value))?.value;
				setCurrentValue(optionValue || null);
				onChange?.(optionValue || null);
			},
			[]
		);

		return (
			<div className={classNames(getInputBaseClassName({ slim: true, variant }), 'dropdown-simple')}>
				{renderLeft ? renderLeft() : null}
				<select
					ref={ref}
					value={String(value)}
					onChange={handleChange}
					defaultValue={defaultValue ? String(defaultValue) : undefined}
				>
					{finalOptions.map(({ value, label }) => (
						<option value={value ? String(value) : undefined}>{label}</option>
					))}
				</select>
			</div>
		);
	}
);
